// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import { Form, Select, Switch, Tabs } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import TabPane from "antd/es/tabs/TabPane";

// Style Imports
import "./edit-fund.scss";

// Local Imports
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import Label from "../../components/label/label.component";
import CustomInput from "../../components/custom-input/custom-input.component";
import EditFundService from "./edit-fund.service";

const { Option } = Select;

const EditFund: React.FC = (): ReactElement => {
  const editFundService = new EditFundService();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const { fundId }: { [key: string]: string | undefined } = useParams();
  const [submitLoginForm, setSubmitLoginForm] = useState(false);
  const [isActiveValue, setIsActiveValue] = useState<boolean>(true);

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmitLoginForm(true);
      },
      () => {
        setSubmitLoginForm(false);
      }
    );
  }, [values]);

  const onSubmit = async (values: any) => {
    values.isActive = isActiveValue;
    values.FUND_ID = Number(fundId);
    values.MIN_SUBS_AMT = Number(values?.MIN_SUBS_AMT);
    await editFundService.submitForm(fundId, values);
  };

  return (
    <section className="edit-fund-container flex flex-col">
      <BreadcrumbComponent />
      <div className="flex flex-col bg-white p-[2%] rounded-[20px]">
        <h3 className=" text-black-500 text-base font-semibold border-b-[1px] border-gray-900 pb-3 mb-5">
          Edit fund
        </h3>
        <Form
          form={form}
          className="flex flex-wrap justify-between"
          onFinish={onSubmit}
        >
          <div className="flex-col w-full md:w-[35%]">
            <Label text="Minimum amount" />
            <Form.Item
              name="MIN_SUBS_AMT"
              rules={[
                {
                  required: true,
                  message: "Please input minimum amount!",
                },
              ]}
            >
              <CustomInput placeholder="Enter minimum amount" />
            </Form.Item>
          </div>

          <div className="flex-col w-full md:w-[35%]">
            <Label text="Order" />
            <Form.Item
              name="order"
              rules={[
                {
                  required: true,
                  message: "Please input order!",
                },
              ]}
            >
              <Select showSearch allowClear placeholder="Select order">
                {Array.from({ length: 100 }, (_, index) => index + 1).map(
                  (item) => (
                    <Option value={item} key={item}>
                      <span>{item}</span>
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
          </div>
          <div className="flex-col w-full md:w-[25%]">
            <Label text="Status" />
            <Form.Item name="isActive">
              <div className="switch flex align-center">
                Active{" "}
                <Switch
                  onChange={(checked: boolean) => setIsActiveValue(checked)}
                  className=" mx-3"
                  checked={true}
                />{" "}
                Deactive
              </div>
            </Form.Item>
          </div>
          <div className="tabs flex flex-col w-full">
            <Label text="Disclaimer" />
            <div className="flex column">
              <Tabs type="card">
                <TabPane tab="English" key="1">
                  <Form.Item
                    name="disclaimerEn"
                    rules={[
                      {
                        required: true,
                        message: "Please input english terms and conditions!",
                      },
                    ]}
                  >
                    <ReactQuill theme="snow" />
                  </Form.Item>
                </TabPane>
                <TabPane tab="Arabic" key="2">
                  <Form.Item
                    name="disclaimerAr"
                    rules={[
                      {
                        required: true,
                        message: "Please input arabic terms and conditions!",
                      },
                    ]}
                  >
                    <ReactQuill theme="snow" />
                  </Form.Item>
                </TabPane>
              </Tabs>
            </div>
          </div>

          <div className="flex w-full flex-wrap items-center justify-between mt-11 border-t-[1px] border-gray-900 pt-5">
            <div className="flex w-[28%] md:w-[48%]">
              <span
                className=" cursor-pointer underline text-gray-700"
                onClick={() => form.resetFields()}
              >
                Reset
              </span>
            </div>
            <div className="flex justify-between items-center w-[70%] md:w-[48%]">
              <button
                type="button"
                className="rounded-lg bg-white w-[48%] py-3 text-center outline-0 cursor-pointer text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
                onClick={() => navigate("/funds")}
              >
                Cancel
              </button>
              <button
                className="rounded-lg bg-blue-500 w-[48%] py-3 text-center outline-0 cursor-pointer text-sm font-semibold border-[1px] border-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!submitLoginForm}
              >
                Submit
              </button>
            </div>
          </div>
        </Form>
      </div>
    </section>
  );
};

export default EditFund;
